import { useMemo, useState } from 'react';
import {
  Combobox,
  Option,
  OptionGroup,
  Skeleton,
  SkeletonItem,
  makeStyles,
  mergeClasses,
  tokens,
  Text,
} from '@fluentui/react-components';
import { Folder20Regular, FolderOpen20Regular } from '@fluentui/react-icons';
import { useContainers } from '@/hooks/useContainers';
import { useFolders } from '@/hooks/useFolders';
import { buildFolderTree } from '@/features/tree/buildTree';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import {
  filterFolderOptions,
  flattenFolderOptions,
  normalize,
  type FolderOption,
} from './folderOptions';
import { filterListableContainers } from '@/utils/listableContainer';
import type { Container } from '@/types/domain';

// Choix « espace + dossier », partagé par « Ranger » (volet) et « Envoyer et
// ranger » (fenêtre de rédaction). Ces deux écrans posent la MÊME question — où
// ranger ? — et deux copies du même sélecteur auraient divergé au premier
// ajustement.
//
// Composant CONTRÔLÉ : il ne décide de rien, il ne range rien. Le parent garde la
// destination et l'action, ce qui est nécessaire ici — l'un range une sélection,
// l'autre envoie d'abord un brouillon.
//
// CE QUE LA REPRISE DU 2026-07-30 CORRIGE, tout venant du même défaut de départ —
// une liste déroulante brute pour deux questions qui n'en sont pas :
//
//  1. LE DOSSIER CHOISI ÉTAIT AMBIGU. La liste indentait les sous-dossiers, mais
//     le champ refermé n'affichait que le nom, précédé de ses espaces
//     d'indentation. Un espace comportant deux « CCRS » à des niveaux différents
//     — cas réel — ne permettait pas de savoir lequel était sélectionné. Le champ
//     affiche désormais le CHEMIN COMPLET.
//  2. AUCUNE RECHERCHE SUR LES DOSSIERS. Le champ des espaces filtrait déjà (140
//     espaces l'imposaient) ; celui des dossiers, non — il fallait parcourir des
//     dizaines de lignes à l'œil. La recherche porte sur le chemin entier et
//     accepte les mots dans n'importe quel ordre.
//  3. IMPASSE SUR UN ESPACE SANS DOSSIER. Le message disait « Aucun dossier dans
//     ce projet. Revenez à la liste pour en créer un. » — c'est-à-dire :
//     abandonnez. Or ranger à la RACINE d'un espace est parfaitement licite et
//     l'API le fait depuis toujours. C'est une entrée du sélecteur.
//  4. LES RÉCENTS ÉTAIENT INVISIBLES. Ils étaient bien en tête de liste, mais
//     rien ne le disait sauf une phrase sous le champ. Ce sont deux groupes
//     nommés.

const useStyles = makeStyles({
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  label: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  // Une option = une ligne : nom, puis précisions en gris. `minWidth: 0` pour que
  // l'ellipse joue dans un volet étroit au lieu de pousser la liste en largeur.
  opt: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, minWidth: 0 },
  optName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  optDim: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexShrink: 1,
  },
  rootOpt: { fontWeight: tokens.fontWeightSemibold },
});

/** Plafond d'options affichées : au-delà, la liste devient illisible. */
const MAX_OPTIONS = 50;

/** Valeur d'option réservée à « la racine de l'espace ». Aucun uuid ne lui ressemble. */
const ROOT_VALUE = '__root__';
const ROOT_LABEL = 'Racine de l’espace';

export interface Destination {
  containerId: string | null;
  folderId: string | null;
  /**
   * Vrai quand l'utilisateur a EXPLICITEMENT choisi de ranger à la racine de
   * l'espace.
   *
   * NÉCESSAIRE, et pas redondant avec `folderId === null` : sans lui, « je n'ai
   * pas encore choisi » et « je choisis la racine » sont le même état, et le
   * bouton d'action ne peut pas savoir s'il doit être actif.
   */
  atRoot: boolean;
}

export const EMPTY_DESTINATION: Destination = {
  containerId: null,
  folderId: null,
  atRoot: false,
};

/** La destination est-elle utilisable ? Un espace, et un dossier OU sa racine. */
export function isDestinationReady(d: Destination): boolean {
  return Boolean(d.containerId) && (d.atRoot || Boolean(d.folderId));
}

export function DestinationPicker({
  value,
  onChange,
}: {
  value: Destination;
  onChange: (next: Destination) => void;
}) {
  const styles = useStyles();
  const { data: containers, isPending: containersLoading } = useContainers();
  const recent = useRecentProjectsStore((s) => s.recent);
  const [containerQuery, setContainerQuery] = useState('');
  const [folderQuery, setFolderQuery] = useState('');

  const { data: folders, isPending: foldersLoading } = useFolders(value.containerId);
  const options = useMemo(
    () => (folders ? flattenFolderOptions(buildFolderTree(folders)) : []),
    [folders],
  );

  // `containers ?? []` fabriquait un tableau NEUF à chaque rendu tant que la
  // requête n'avait pas répondu, ce qui invalidait en cascade tout ce qui en
  // dépend. Mémoïsé, il devient une dépendance stable.
  // `filterListableContainers` rend le tableau d'entrée tel quel quand rien n'est
  // à retirer (le cas normal, le serveur filtrant déjà) : la mémoïsation reste
  // donc aussi stable qu'avant.
  const all = useMemo(
    () => filterListableContainers((containers ?? []) as Container[]) as Container[],
    [containers],
  );
  // Mémoïsée pour être une dépendance HONNÊTE du tri ci-dessous : recréée à chaque
  // rendu, cette Map rendait la liste de dépendances incomplète.
  const byId = useMemo(() => new Map(all.map((c) => [c.id, c])), [all]);

  const recentContainers = useMemo(
    () => recent.map((r) => byId.get(r.id)).filter((c): c is Container => Boolean(c)),
    [recent, byId],
  );
  const otherContainers = useMemo(() => {
    const recentSet = new Set(recent.map((r) => r.id));
    return all
      .filter((c) => !recentSet.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [all, recent]);

  const selectedContainer = value.containerId ? byId.get(value.containerId) ?? null : null;

  // LE FILTRE EST NEUTRALISÉ QUAND LE TEXTE EST EXACTEMENT LE NOM DÉJÀ CHOISI.
  // Après une sélection, le champ affiche ce nom ; s'il continuait de filtrer,
  // rouvrir la liste ne proposerait plus que cet unique espace, et changer d'avis
  // obligerait à effacer le champ à la main. Défaut constaté au banc d'essai.
  const containerNeedle = useMemo(() => {
    const raw = containerQuery.trim();
    return raw && raw !== selectedContainer?.name ? normalize(raw) : '';
  }, [containerQuery, selectedContainer]);

  const matches = (c: Container) =>
    !containerNeedle ||
    normalize(c.name).includes(containerNeedle) ||
    (c.number ? normalize(c.number).includes(containerNeedle) : false);

  const filteredRecent = recentContainers.filter(matches);
  const filteredOthers = otherContainers.filter(matches);
  const totalShown = filteredRecent.length + filteredOthers.length;

  const selectedFolder = options.find((f) => f.id === value.folderId) ?? null;
  const folderLabel = value.atRoot ? ROOT_LABEL : selectedFolder?.path ?? '';

  const folderNeedle = useMemo(() => {
    const raw = folderQuery.trim();
    return raw && raw !== folderLabel ? raw : '';
  }, [folderQuery, folderLabel]);

  const filteredFolders = useMemo(
    () => filterFolderOptions(options, folderNeedle),
    [options, folderNeedle],
  );
  // La racine reste proposée tant que la recherche ne l'exclut pas — c'est une
  // destination comme une autre, et la seule disponible dans un espace vide.
  const rootMatches = !folderNeedle || normalize(ROOT_LABEL).includes(normalize(folderNeedle));

  const selectContainer = (containerId: string | null) => {
    // Le dossier est REMIS À ZÉRO avec l'espace : garder l'ancien laisserait une
    // destination qui n'existe pas dans le nouvel espace, et le bouton d'action
    // semblerait actif à tort. La recherche de dossier est effacée pour la même
    // raison — elle portait sur l'arborescence précédente.
    onChange({ containerId, folderId: null, atRoot: false });
    setContainerQuery(containerId ? byId.get(containerId)?.name ?? '' : '');
    setFolderQuery('');
  };

  const renderContainer = (c: Container) => {
    // Le numéro n'est répété QUE s'il ne figure pas déjà dans le nom. Dans ce
    // tenant, la plupart des noms commencent par leur numéro (« 19048 - NIMES… »),
    // et l'ancien rendu affichait « 19048 - NIMES… · 19048 ».
    const showNumber =
      c.kind === 'project' && c.number && !normalize(c.name).includes(normalize(c.number));
    return (
      <Option key={c.id} value={c.id} text={c.name}>
        <span className={styles.opt}>
          <span className={styles.optName}>{c.name}</span>
          {showNumber && <span className={styles.optDim}>n° {c.number}</span>}
        </span>
      </Option>
    );
  };

  return (
    <>
      <div className={styles.group}>
        <Text className={styles.label}>Projet ou espace</Text>
        {containersLoading ? (
          <Skeleton>
            <SkeletonItem size={32} />
          </Skeleton>
        ) : (
          <Combobox
            freeform
            clearable
            placeholder="Nom ou numéro du projet…"
            value={containerQuery || selectedContainer?.name || ''}
            selectedOptions={value.containerId ? [value.containerId] : []}
            onInput={(e) => setContainerQuery((e.target as HTMLInputElement).value)}
            onOptionSelect={(_e, data) => selectContainer(data.optionValue ?? null)}
          >
            {totalShown === 0 && (
              <Option value="" text="" disabled>
                Aucun espace ne correspond
              </Option>
            )}
            {filteredRecent.length > 0 && (
              <OptionGroup label="Récents">{filteredRecent.map(renderContainer)}</OptionGroup>
            )}
            {filteredOthers.length > 0 && (
              <OptionGroup label={filteredRecent.length > 0 ? 'Tous les espaces' : 'Espaces'}>
                {filteredOthers.slice(0, MAX_OPTIONS).map(renderContainer)}
              </OptionGroup>
            )}
            {filteredOthers.length > MAX_OPTIONS && (
              <Option value="" text="" disabled>
                {filteredOthers.length - MAX_OPTIONS} autres espaces — affinez la recherche
              </Option>
            )}
          </Combobox>
        )}
      </div>

      <div className={styles.group}>
        <Text className={styles.label}>Dossier</Text>
        {!value.containerId && (
          <Text className={styles.hint}>Choisissez d’abord un projet ou un espace.</Text>
        )}
        {value.containerId && foldersLoading && (
          <Skeleton>
            <SkeletonItem size={32} />
          </Skeleton>
        )}
        {value.containerId && !foldersLoading && (
          <>
            <Combobox
              freeform
              clearable
              placeholder={
                options.length === 0 ? 'Racine de l’espace' : 'Nom du dossier, ou du chemin…'
              }
              value={folderQuery || folderLabel}
              selectedOptions={
                value.atRoot ? [ROOT_VALUE] : value.folderId ? [value.folderId] : []
              }
              onInput={(e) => setFolderQuery((e.target as HTMLInputElement).value)}
              onOptionSelect={(_e, data) => {
                const v = data.optionValue ?? null;
                if (v === ROOT_VALUE) {
                  onChange({ containerId: value.containerId, folderId: null, atRoot: true });
                  setFolderQuery(ROOT_LABEL);
                  return;
                }
                onChange({ containerId: value.containerId, folderId: v, atRoot: false });
                setFolderQuery(v ? options.find((f) => f.id === v)?.path ?? '' : '');
              }}
            >
              {rootMatches && (
                <Option value={ROOT_VALUE} text={ROOT_LABEL}>
                  <span className={mergeClasses(styles.opt, styles.rootOpt)}>
                    <FolderOpen20Regular />
                    <span className={styles.optName}>{ROOT_LABEL}</span>
                  </span>
                </Option>
              )}
              {filteredFolders.slice(0, MAX_OPTIONS).map((f) => (
                <FolderOptionRow key={f.id} option={f} styles={styles} />
              ))}
              {!rootMatches && filteredFolders.length === 0 && (
                <Option value="" text="" disabled>
                  Aucun dossier ne correspond
                </Option>
              )}
              {filteredFolders.length > MAX_OPTIONS && (
                <Option value="" text="" disabled>
                  {filteredFolders.length - MAX_OPTIONS} autres dossiers — affinez la recherche
                </Option>
              )}
            </Combobox>
            <Text className={styles.hint}>
              {options.length === 0
                ? 'Cet espace n’a aucun dossier : le mail sera rangé à sa racine.'
                : `${options.length} dossier(s). La racine de l’espace est une destination valable.`}
            </Text>
          </>
        )}
      </div>
    </>
  );
}

/**
 * Une ligne de la liste des dossiers : indentation par la profondeur, nom, et —
 * seulement si le nom est ambigu dans cet espace — le chemin du parent en gris.
 *
 * L'indentation est une MARGE et non des espaces insécables : le `text` de
 * l'option sert à la saisie clavier de Fluent, il ne doit pas contenir de
 * remplissage. C'était le cas avant, et le champ refermé affichait ces espaces.
 */
function FolderOptionRow({
  option,
  styles,
}: {
  option: FolderOption;
  styles: Record<string, string>;
}) {
  return (
    <Option value={option.id} text={option.path}>
      <span className={styles.opt} style={{ paddingLeft: `${option.depth * 12}px` }}>
        <Folder20Regular />
        <span className={styles.optName}>{option.name}</span>
        {option.ambiguous && option.parentPath && (
          <span className={styles.optDim}>dans {option.parentPath}</span>
        )}
      </span>
    </Option>
  );
}
